import type { JobTicketDto } from "@/model/worker/job-ticket-dto"
import { type HttpContent, useDialogs, useHttpClient } from "@/vf"
import type { AxiosError } from "axios"
import { computed, ref } from "vue"

/**
 * The background runs the current user triggered — of every job kind — shared by the tray that
 * shows them and the pages that start them.
 *
 * Module scope on purpose: there is one tray in the app shell, and whoever starts a run wants
 * it to appear there right away instead of on some later poll.
 */
const tickets = ref<JobTicketDto[]>([])

/** A run that ended — successfully or not. */
export function isSettled(ticket: JobTicketDto): boolean {
    return ticket.status === "Succeeded" || ticket.status === "Failed" || ticket.status === "Cancelled"
}

/** A run that still has something ahead of it. */
export function isOpen(ticket: JobTicketDto): boolean {
    return !isSettled(ticket)
}

/** Runs that still have something ahead of them. */
const hasOpen = computed(() => tickets.value.some(isOpen))

/** While something is open its progress is worth watching closely. */
const BUSY_INTERVAL = 3000

/**
 * Otherwise the poll only has to notice runs this tab did not start — one begun in another tab,
 * or one another tab already downloaded. Rare, so rarely asked.
 */
const IDLE_INTERVAL = 20000

let timer: ReturnType<typeof setTimeout> | null = null
let enabled = false
let tick: (() => Promise<void>) | null = null

/**
 * Schedules the next poll, replacing whatever was pending: an interval chosen while nothing was
 * running must not outlive the moment a run starts.
 */
function rearm() {
    if (!enabled || tick === null) {
        return
    }

    if (timer !== null) {
        clearTimeout(timer)
    }

    timer = setTimeout(tick, hasOpen.value ? BUSY_INTERVAL : IDLE_INTERVAL)
}

export function useJobTray() {
    const http = useHttpClient()
    const dialogs = useDialogs()

    async function refresh() {
        try {
            const mine = (await http.getBg<JobTicketDto[]>("/worker/job/mine")).data

            // stop() may have run while this was in flight; it emptied the list on purpose
            if (enabled) {
                tickets.value = mine
            }
        } catch {
            // A hiccup while polling must not make the tray flicker away
        }
    }

    /**
     * Queues a run and puts it in the tray right away instead of on the next poll.
     *
     * Every start endpoint checks up front whether there is anything to do at all and answers a
     * refused start with a 400 carrying the reason — that reason is the whole point of the
     * check, so it is shown here. Without it a button behind which nothing happens looks broken.
     */
    async function startJob(url: string, body?: HttpContent): Promise<boolean> {
        try {
            await http.post(url, body)
        } catch (e) {
            const error = e as AxiosError<{ error: string }>
            void dialogs.error({ message: error.response?.data?.error ?? "Der Vorgang konnte nicht gestartet werden." })

            return false
        }

        await refresh()
        // The pending tick was scheduled while nothing was running, so it is 20s out. Without
        // re-arming, the run that was just started would sit still for that long before its
        // progress starts moving
        rearm()

        return true
    }

    /** Drops a run locally; the server has already been told, this just saves waiting for a poll. */
    function forget(id: string) {
        tickets.value = tickets.value.filter(t => t.id !== id)
    }

    async function dismiss(id: string) {
        forget(id)
        await http.post(`/worker/job/${id}/dismiss`)
    }

    /**
     * Asks a run to stop. Only a request: the handlers see it at their next checkpoint, so the
     * tile keeps showing progress until the run really ends — it says "wird abgebrochen" in the
     * meantime, which is why the flag is set locally instead of waiting for the next poll.
     */
    async function cancel(id: string) {
        const ticket = tickets.value.find(t => t.id === id)

        if (ticket) {
            ticket.isCancelRequested = true
        }

        await http.post(`/worker/job/${id}/cancel`)
    }

    /**
     * Keeps the list up to date for as long as the user is logged in.
     *
     * Deliberately a plain timer in module scope rather than useIntervalFn: that one is bound to
     * the scope of whichever component happens to call it first, and would die silently when that
     * component unmounts while the tray in the app shell lives on.
     *
     * It also keeps polling when nothing is running — otherwise a run started somewhere else stays
     * invisible until the next reload, which is exactly the case that looks like a broken tray.
     * Only while the tab is visible; a background tab has nobody watching its progress bar.
     */
    function start() {
        if (enabled) {
            return
        }

        enabled = true

        tick = async () => {
            if (document.visibilityState === "visible") {
                await refresh()
            }

            // stop() may have run while the refresh was in flight. Re-arming here would keep
            // polling for a logged out user, and the next start() would add a second chain on
            // top of this one
            if (!enabled) {
                return
            }

            rearm()
        }

        void tick()
    }

    function stop() {
        enabled = false
        tickets.value = []

        if (timer !== null) {
            clearTimeout(timer)
            timer = null
        }
    }

    return { tickets, hasOpen, refresh, startJob, dismiss, cancel, forget, start, stop } as const
}
